import React, { useEffect, useState } from "react";
import CustomButton from "../../../components/form/CustomButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showFormErrors } from "../../../helpers/showFormErrors";
import { paymentsMethodsResource } from "../../../helpers/configRoutes/backend_helper";
import BaseModalAnimated from "../../../components/modal/BaseModalAnimated";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import CustomInput from "../../../components/form/CustomInput";

const AddUpdMethodsModal = ({ modal, setModal, item, refresh }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);

    const baseItem = {
        id: null,
        card_name: "",
        alias: "",
        principal: false,
    };

    const validation = useFormik({
        initialValues: baseItem,
        enableReinitialize: true,
        validationSchema: Yup.object({
            card_name: Yup.string().required(
                "Card holder name is required."
            ),
            alias: Yup.string().nullable(),
            principal: Yup.boolean(),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);

            try {
                if (!stripe || !elements) {
                    setErrors({
                        card_name: "Stripe is not available at the moment.",
                    });
                    setLoading(false);
                    return;
                }

                const cardElement = elements.getElement(CardElement);
                if (!cardElement) {
                    setErrors({
                        card_name: "Unable to read card information.",
                    });
                    setLoading(false);
                    return;
                }

                const { error, paymentMethod } =
                    await stripe.createPaymentMethod({
                        type: "card",
                        card: cardElement,
                        billing_details: { name: values.card_name },
                    });

                if (error) {
                    throw new Error(error.message);
                }

                const dataToSend = {
                    ...values,
                    paymentMethodId: paymentMethod.id,
                    last4:paymentMethod.card.last4,
                    exp_month:paymentMethod.card.exp_month,
                    exp_year: paymentMethod.card.exp_year
                };

                console.log(dataToSend)

                const response = await paymentsMethodsResource(
                    values.id ? "put" : "post",
                    dataToSend,
                    values.id
                );

                if (!response.status) {
                    throw new Error(
                        response.errors || "Error saving payment method."
                    );
                }

                if (typeof refresh === "function") {
                    await refresh();
                }

                setModal({ ...modal, show: false });
            } catch (err) {
                setErrors({ card_name: err.message });
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        if (item && item.id) {
            validation.setValues({
                ...baseItem,
                ...item,
            });
        } else {
            validation.resetForm({ values: baseItem });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item, modal.show]);

    const title =
        modal.title || (item?.id ? "Edit payment method" : "Add payment method");

    return (
        <BaseModalAnimated
            modal={modal}
            setModal={setModal}
            title={title}
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    showFormErrors(validation);
                    return false;
                }}
            >
                <div className="bg-cs-bg rounded-2xl overflow-hidden">
                    {/* Header contextual */}
                    <div className="px-5 py-3 border-b border-cs-border bg-cs-surface flex items-center justify-between gap-3">
                        <div className="flex flex-col gap-0.5">
                            <p className="text-[10px] uppercase tracking-[0.16em] text-cs-primary-dark">
                                {item?.id
                                    ? "Update saved card"
                                    : "Add new payment method"}
                            </p>
                            <p className="text-[9px] text-cs-muted">
                                Your card data is processed securely via Stripe.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setModal({ ...modal, show: false })}
                            className="
                                px-3 py-1.5 rounded-xl
                                bg-cs-bg
                                text-[9px] text-cs-muted
                                hover:bg-cs-primary-soft hover:text-cs-primary
                                border border-cs-border
                                transition-all
                            "
                        >
                            Close
                        </button>
                    </div>

                    {/* Body */}
                    <div className="p-5 bg-cs-bg-alt grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Card holder */}
                        <CustomInput
                            label="Card holder name"
                            type="text"
                            name="card_name"
                            labelClassName="text-[10px] font-medium text-cs-muted mb-1"
                            error={validation.errors.card_name}
                            value={validation.values.card_name}
                            handleChange={validation.handleChange}
                            className="text-[11px]"
                            placeholder="As shown on the card"
                        />

                        {/* Card info (Stripe) */}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-medium text-cs-muted">
                                Card information
                            </label>
                            <div
                                className="
                                    w-full py-2 px-2.5 rounded-xl
                                    border border-cs-border
                                    bg-cs-bg
                                    text-[10px]
                                    focus-within:border-cs-primary
                                    transition-colors
                                "
                            >
                                <CardElement
                                    options={{
                                        style: {
                                            base: {
                                                fontSize: "12px",
                                                color: "#151515",
                                                "::placeholder": {
                                                    color: "#9CA3AF",
                                                },
                                            },
                                            invalid: {
                                                color: "#EF4444",
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        {/* Alias */}
                        <CustomInput
                            label="Alias (optional)"
                            type="text"
                            name="alias"
                            labelClassName="text-[10px] font-medium text-cs-muted mb-1"
                            error={validation.errors.alias}
                            value={validation.values.alias || ""}
                            handleChange={validation.handleChange}
                            className="text-[11px]"
                            placeholder="e.g. Main card, Operations, Accounting..."
                        />

                        {/* Primary radio */}
                        <div className="flex flex-col gap-1 px-0 md:px-1">
                            <span className="text-[10px] font-medium text-cs-muted">
                                Primary payment method
                            </span>
                            <div className="flex items-center gap-4 mt-1 text-[9px]">
                                <label className="flex items-center gap-1.5">
                                    <input
                                        type="radio"
                                        name="principal"
                                        value="true"
                                        checked={
                                            validation.values.principal ===
                                            true
                                        }
                                        onChange={() =>
                                            validation.setFieldValue(
                                                "principal",
                                                true
                                            )
                                        }
                                        className="w-3 h-3"
                                    />
                                    <span className="text-cs-dark">
                                        Yes, set as primary
                                    </span>
                                </label>
                                <label className="flex items-center gap-1.5">
                                    <input
                                        type="radio"
                                        name="principal"
                                        value="false"
                                        checked={
                                            validation.values.principal ===
                                            false
                                        }
                                        onChange={() =>
                                            validation.setFieldValue(
                                                "principal",
                                                false
                                            )
                                        }
                                        className="w-3 h-3"
                                    />
                                    <span className="text-cs-muted">
                                        No, keep as secondary
                                    </span>
                                </label>
                            </div>
                            {validation.errors.principal && (
                                <p className="text-cs-danger text-[8px] mt-1">
                                    {validation.errors.principal}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-5 py-4 bg-cs-bg border-t border-cs-border flex justify-end">
                        <CustomButton
                            loading={loading}
                            textLoad="Saving..."
                            type="success"
                            className="
                                w-auto px-5 py-2
                                bg-cs-primary
                                hover:bg-cs-primary-hover
                                text-white
                                rounded-xl
                                text-[10px] font-medium
                                shadow-sm hover:shadow-md
                            "
                        >
                            {validation.values.id
                                ? "Save changes"
                                : "Add method"}
                        </CustomButton>
                    </div>
                </div>
            </form>
        </BaseModalAnimated>
    );
};

export default AddUpdMethodsModal;
